import type { ChangeEventHandler } from 'react';

import Badge from 'components/Badge';
import Button from 'components/Button';
import { RadioGroup } from 'components/FormUi';
import Icon from 'components/Icon';
import { Checkbox } from 'components/InputUi';
import PlainButton from 'components/PlainButton';
import Popover from 'components/Popover';
import type { FacetResponse, ProductListSortOption } from 'models/api';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';
import { slugify } from 'utils/string';

/** Fake facet ID for the sort options. */
export const SORT_FACET_ID = 'Sort';

interface Props {
	disabled?: boolean;
	facets: FacetResponse[];
	isOpen: boolean;
	onClose: () => void;
	onFacetChange: ChangeEventHandler<HTMLInputElement>;
	onFacetSelect: (facetId: string) => void;
	onSortChange: ChangeEventHandler<HTMLInputElement>;
	selectedFacetId: string;
	selectedFacetValues: [string, string][];
	selectedSortOptionValue: string;
	sortOptions: ProductListSortOption[];
	totalProductsCount: number;
}

/** Popover with filtering and sorting controls. */
export default function ProductListFiltersPopover({
	disabled,
	facets: facetsProp,
	isOpen,
	onClose,
	onFacetChange,
	onFacetSelect,
	onSortChange,
	selectedFacetId,
	selectedFacetValues,
	selectedSortOptionValue,
	sortOptions,
	totalProductsCount,
}: Props) {
	const { t } = useI18n();

	if (sortOptions.length === 0 && facetsProp.length === 0) {
		return null;
	}

	// Fake a facet for the sort options to treat everything the same.
	const sortFacet: FacetResponse = {
		facetItems: sortOptions.map((opt) => ({
			item: opt.attribute,
			// Won't be used
			count: 0,
		})),
		facetType: SORT_FACET_ID,
		id: SORT_FACET_ID,
		title: t('product_list_sorting_facet_heading'),
	};
	const facets =
		sortOptions.length > 0 ? [sortFacet, ...facetsProp] : facetsProp;

	const selectedFacet = facets.find((facet) => facet.id === selectedFacetId);
	const selectedSortOption = sortOptions.find(
		(opt) => opt.attribute === selectedSortOptionValue,
	);
	const baseId = 'product-list-filters-popover';

	return (
		<Popover
			id={baseId}
			isOpen={isOpen}
			openingFocusTargetRoot="content"
			currentPageIndex={selectedFacet ? 1 : 0}
			onClose={onClose}
			backButtonText={t('product_list_filters_popover_back_button')}
			onBackClick={() => {
				onFacetSelect('');
			}}
			pages={[
				{
					title: t('product_list_filters_heading'),
					content: (
						<div className="-mt-4">
							{facets.map((facet) => {
								const isSort = facet.id === SORT_FACET_ID;
								const selected = isSort
									? [selectedSortOption?.name].filter(is.truthy)
									: selectedFacetValues
											.filter(([key]) => key === facet.id)
											.map(([, val]) => val);
								const selectedCount = selected.length;
								const hasSelected = selectedCount > 0;

								return (
									<PlainButton
										key={facet.id}
										onClick={() => {
											onFacetSelect(facet.id);
										}}
										className="group/filter-item block w-full border-b border-b-subtle py-4 text-left"
										aria-label={[
											hasSelected ? `${facet.title}:` : facet.title,
											hasSelected && !isSort && `(${selectedCount})`,
											hasSelected && selected.join(', '),
										]
											.filter(Boolean)
											.join(' ')}
									>
										<span className="flex items-center">
											<span className="mr-4 font-bold group-hover/filter-item:underline">
												{facet.title}
											</span>
											{hasSelected && !isSort && (
												<Badge text={selectedCount} className="mr-4 -ml-2" />
											)}
											<Icon name="chevron-right" className="ml-auto" />
										</span>
										{hasSelected && (
											<span className="block pr-8 text-sm">
												{selected.join(', ')}
											</span>
										)}
									</PlainButton>
								);
							})}
						</div>
					),
				},
				{
					// In practice there will always be a facet when this page is shown
					// so the empty string fallback will never be used.
					title: selectedFacet?.title ?? '',
					content: (
						<>
							{selectedFacetId === SORT_FACET_ID && (
								<RadioGroup
									id={`${baseId}-${SORT_FACET_ID}`}
									name={`${baseId}-${SORT_FACET_ID}`}
									legend={t('product_list_sorting_facet_heading')}
									hiddenLegend
									className="mt-6"
									value={selectedSortOptionValue}
									onChange={onSortChange}
									options={sortOptions.map((opt, i) => ({
										label: opt.name,
										value: opt.attribute,
										className: i === 0 ? undefined : 'mt-6',
										labelClassName: 'grow',
										disabled,
									}))}
								/>
							)}
							{selectedFacetId !== SORT_FACET_ID && selectedFacet && (
								<fieldset className="mt-6">
									<legend className="sr-only">{selectedFacet.title}</legend>
									{selectedFacet.facetItems?.map((item, i) => (
										<Checkbox
											key={item.item}
											id={slugify(`${baseId}-${selectedFacet.id}-${item.item}`)}
											name={selectedFacet.id}
											value={item.item}
											checked={selectedFacetValues.some(
												([key, val]) =>
													key === selectedFacet.id && val === item.item,
											)}
											disabled={disabled}
											onChange={onFacetChange}
											className={i === 0 ? undefined : 'mt-6'}
											labelClassName="grow"
											label={`${item.item} (${item.count})`}
										/>
									))}
								</fieldset>
							)}
						</>
					),
				},
			]}
			padFooter
			footer={
				<Button variant="branded" displayWidth="full" onClick={onClose}>
					{t('product_list_show_products_button')} ({totalProductsCount})
				</Button>
			}
		/>
	);
}
ProductListFiltersPopover.displayName = 'ProductListFiltersPopover';
